import React, { useState, useEffect } from 'react';
import { authService, storeService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassPanel, GlassButton, GlassInput, GlassSelect, GlassCard, PageHeader } from '../components/GlassUI';
import { subscriptionService } from '../services/api';
import { FaUser, FaSave, FaDatabase, FaTrash, FaShieldAlt, FaStore, FaPhone, FaWhatsapp, FaGlobe, FaMapPin, FaQrcode, FaShareAlt, FaDownload } from 'react-icons/fa';
import QrCode from '../components/QrCode';
import { downloadVCard, shareVCard } from '../utils/download';

export const ProfilePage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    staffName: '',
    phoneNumber: '',
    storeType: 'OFFLINE',
  });

  // ─── Shop / company details (shown to customers on the QR pages) ───
  const [storeData, setStoreData] = useState({
    companyName: 'Apni Jewellers',
    phone: '',
    whatsapp: '',
    website: '',
    address: '',
  });
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeMessage, setStoreMessage] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        staffName: user.staffName || '',
        phoneNumber: user.phoneNumber || '',
        storeType: user.storeType || 'OFFLINE',
      });
      loadProfile();
      loadStore();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const res = await authService.getProfile();
      setFormData({
        name: res.data.name || '',
        staffName: res.data.staffName || '',
        phoneNumber: res.data.phoneNumber || '',
        storeType: res.data.storeType || 'OFFLINE',
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const loadStore = async () => {
    try {
      const res = await storeService.get();
      const s = res.data?.store;
      if (s) {
        setStoreData({
          companyName: s.companyName || '',
          phone: s.phone || '',
          whatsapp: s.whatsapp || '',
          website: s.website || '',
          address: s.address || '',
        });
      }
    } catch (error) {
      console.error('Error loading shop details:', error);
    }
  };

  const handleStoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStoreData(prev => ({ ...prev, [name]: value }));
  };

  const handleStoreSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeData.companyName.trim()) {
      alert('Company / shop name is required');
      return;
    }
    setStoreLoading(true);
    setStoreMessage('');
    try {
      const res = await storeService.update(storeData);
      const s = res.data?.store;
      if (s) {
        setStoreData({
          companyName: s.companyName || '',
          phone: s.phone || '',
          whatsapp: s.whatsapp || '',
          website: s.website || '',
          address: s.address || '',
        });
      }
      setStoreMessage('Shop details updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update shop details');
    } finally {
      setStoreLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const res = await authService.updateProfile(formData);
      setUser(res.data.user);
      setMessage('Profile updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // ─── QR codes for the shop (self-registration + save contact) ───
  const shopName = storeData.companyName || 'Our Store';
  const storeUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?source=QR_SCAN`;
  const vCardData = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${shopName}`,
    `ORG:${shopName}`,
    storeData.phone ? `TEL;TYPE=WORK,VOICE:${storeData.phone}` : '',
    storeData.whatsapp ? `TEL;TYPE=WHATSAPP:${storeData.whatsapp}` : '',
    storeData.website ? `URL:${storeData.website}` : '',
    storeData.address ? `ADR:;;${storeData.address};;;;` : '',
    'END:VCARD',
  ].filter(Boolean).join('\n');
  const contactFile = `${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact`;

  return (
    <div className='max-w-2xl mx-auto text-slate-900'>
        <PageHeader
          title='Profile'
          subtitle='View and edit your account details.'
          icon={<FaUser />}
        />

        <GlassPanel className='p-8'>
          <form onSubmit={handleSubmit} className='space-y-5'>
            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Email</label>
              <GlassInput value={user?.email || ''} disabled className='bg-slate-50' />
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Full Name</label>
              <GlassInput
                name='name' value={formData.name}
                onChange={handleChange} placeholder='Full name' required
              />
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Staff Name (for bills)</label>
              <GlassInput
                name='staffName' value={formData.staffName}
                onChange={handleChange} placeholder='Staff display name'
              />
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Phone Number</label>
              <GlassInput
                name='phoneNumber' value={formData.phoneNumber}
                onChange={handleChange} placeholder='Phone number'
              />
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>Store Type</label>
              <GlassSelect name='storeType' value={formData.storeType} onChange={handleChange}>
                <option value='OFFLINE'>Offline</option>
                <option value='ONLINE'>Online</option>
                <option value='BOTH'>Both</option>
              </GlassSelect>
            </div>

            {message && (
              <GlassCard className='bg-green-50 border-green-200 text-green-800'>
                <p className='flex items-center gap-2'><FaSave /> {message}</p>
              </GlassCard>
            )}

            <GlassButton type='submit' variant='primary' className='w-full' disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </GlassButton>
          </form>
        </GlassPanel>

        {/* Business / Shop Details */}
        <GlassPanel className='p-8 mt-6'>
          <h2 className='text-lg font-bold text-slate-900 mb-1 flex items-center gap-2'>
            <FaStore className='text-blue-500' /> Business / Shop Details
          </h2>
          <p className='text-sm text-slate-500 mb-5'>
            These are your shop&apos;s own contact details. They appear on the customer self-registration page and in
            the &ldquo;Save Our Contact&rdquo; QR code, so customers save the correct number.
          </p>

          <form onSubmit={handleStoreSubmit} className='space-y-5'>
            <div>
              <label className='block text-slate-700 font-semibold mb-2'>
                <FaStore className='inline mr-1 text-slate-400' /> Company / Shop Name *
              </label>
              <GlassInput
                name='companyName' value={storeData.companyName}
                onChange={handleStoreChange} placeholder='e.g., Apni Jewellers' required
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>
                  <FaPhone className='inline mr-1 text-slate-400' /> Contact Number
                </label>
                <GlassInput
                  name='phone' value={storeData.phone}
                  onChange={handleStoreChange} placeholder='+91 XXXXX XXXXX'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>
                  <FaWhatsapp className='inline mr-1 text-green-500' /> WhatsApp Number
                </label>
                <GlassInput
                  name='whatsapp' value={storeData.whatsapp}
                  onChange={handleStoreChange} placeholder='Same as contact if blank'
                />
              </div>
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>
                <FaGlobe className='inline mr-1 text-slate-400' /> Website
              </label>
              <GlassInput
                name='website' value={storeData.website}
                onChange={handleStoreChange} placeholder='https://example.com'
              />
            </div>

            <div>
              <label className='block text-slate-700 font-semibold mb-2'>
                <FaMapPin className='inline mr-1 text-slate-400' /> Address
              </label>
              <GlassInput
                name='address' value={storeData.address}
                onChange={handleStoreChange} placeholder='Shop address'
              />
            </div>

            {storeMessage && (
              <GlassCard className='bg-green-50 border-green-200 text-green-800'>
                <p className='flex items-center gap-2'><FaSave /> {storeMessage}</p>
              </GlassCard>
            )}

            <GlassButton type='submit' variant='primary' className='w-full' disabled={storeLoading}>
              {storeLoading ? 'Saving...' : 'Save Shop Details'}
            </GlassButton>
          </form>
        </GlassPanel>

        {/* QR Codes — registration + save contact */}
        <GlassPanel className='p-6 mt-6'>
          <h2 className='text-lg font-bold text-slate-900 mb-1 flex items-center gap-2'>
            <FaQrcode className='text-purple-500' /> QR Codes
          </h2>
          <p className='text-sm text-slate-500 mb-5'>
            Print the registration QR for your counter. Customers can also scan the contact QR to save your
            number straight to their phone, or use Save Contact / Share below.
          </p>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
            <div className='text-center'>
              <h3 className='font-semibold text-slate-800 mb-2'>Customer Registration</h3>
              <QrCode
                value={storeUrl}
                size={160}
                fileName='customer-registration-qr'
                downloadLabel='Download'
                showShare
                shareTitle={`Register at ${shopName}`}
              />
            </div>
            <div className='text-center'>
              <h3 className='font-semibold text-slate-800 mb-2'>Save Our Contact</h3>
              <QrCode
                value={vCardData}
                size={160}
                fileName={`${contactFile}-qr`}
                downloadLabel='Download'
                showShare
                shareTitle={`Save ${shopName} as a contact`}
              />
            </div>
          </div>

          <div className='flex flex-col sm:flex-row gap-3 mt-6'>
            <GlassButton
              variant='primary'
              className='flex-1'
              onClick={() => downloadVCard(contactFile, vCardData)}
            >
              <FaDownload /> Save Contact (vCard)
            </GlassButton>
            <GlassButton
              variant='secondary'
              className='flex-1'
              onClick={() => shareVCard(contactFile, vCardData, `${shopName} contact`, storeData.website || undefined)}
            >
              <FaShareAlt /> Share Contact
            </GlassButton>
          </div>
        </GlassPanel>

        {/* Data & Backup */}
        <GlassPanel className='p-6 mt-6'>
          <h2 className='text-lg font-bold text-slate-900 mb-1 flex items-center gap-2'>
            <FaDatabase className='text-amber-500' /> Data & Backup
          </h2>
          <p className='text-sm text-slate-500 mb-4'>
            Your data is stored securely. Create manual backups or clear demo data to start fresh.
          </p>

          <div className='flex flex-col sm:flex-row gap-3'>
            <GlassButton
              variant='secondary'
              className='flex-1'
              onClick={async () => {
                try {
                  const res = await subscriptionService.backup();
                  alert(`✅ ${res.data.message}\nFile: ${res.data.backupFile}`);
                } catch (e: any) {
                  alert(e?.response?.data?.error || 'Backup failed');
                }
              }}
            >
              <FaDatabase /> Backup Database Now
            </GlassButton>
            <GlassButton
              variant='secondary'
              className='flex-1 text-red-600 border-red-200 hover:bg-red-50'
              onClick={async () => {
                if (!window.confirm('This removes ALL demo customers, orders, repairs and analytics. Your account stays. Continue?')) return;
                if (!window.confirm('Are you absolutely sure? Take a backup first! This cannot be undone.')) return;
                try {
                  await subscriptionService.backup();
                  const res = await subscriptionService.resetData();
                  alert(`✅ ${res.data.message}`);
                  window.location.reload();
                } catch (e: any) {
                  alert(e?.response?.data?.error || 'Reset failed');
                }
              }}
            >
              <FaTrash /> Clear All Demo Data
            </GlassButton>
          </div>

          <div className='flex items-center gap-2 mt-4 text-xs text-slate-400'>
            <FaShieldAlt className='text-emerald-500' />
            Secure storage · Automatic backups · No data loss on plan changes
          </div>
        </GlassPanel>
    </div>
  );
};

export default ProfilePage;
